import { minBy } from 'es-toolkit';
import paper from 'paper';

import { Line2D, Point2D } from '@paragon/geometry-library';
import { getModifierKeys } from '@paragon/utilities';

import { projectOntoLine, toPaperPoint } from '../geometry/basicOperations';
import {
  CornerGripName,
  getAnnotationGrips,
  getGripUnderPoint,
  getOppositeCorner,
  getOppositeSide,
  GripName,
  isCorner,
  isVertical,
  SideGripName,
} from './annotationGrips';
import { PageImageInfo, RenderedItem, renderTranslationAreaBounds } from './paperItemTranslator';

// boolean flag for mouseup to know if its for a drag or click event
let currentlyDragging = false;
let annotationRotation: number;

const MINIMUM_FONT_SIZE = 4; // pt
const MINIMUM_ITEM_SIZE = 5; // px

// Needed because of https://github.com/paperjs/paper.js/issues/992#issuecomment-188143026
let dialogIsOpen = false;
export function setDialogOpenStatus(newValue: boolean) {
  dialogIsOpen = newValue;
}

export interface ItemEventHandlers {
  readonly singleClick: (item: RenderedItem, ctrlKey: boolean) => void;
  readonly doubleClick: (item: RenderedItem) => void;
  readonly dragItem: (item: RenderedItem) => void;
  readonly dragEnd: (item: RenderedItem) => void;
  readonly mouseUp: (item: RenderedItem, ctrlKey: boolean) => void;
}

// From PaperFunctions > registerItemEvents()
export function registerItemEvents(
  paperItems: readonly RenderedItem[],
  eventHandlers: ItemEventHandlers,
  pageImageInfo: PageImageInfo,
) {
  const selectedItems = paperItems.filter((annotation) => annotation.reduxSelected);

  for (const item of paperItems) {
    // selectedItems and itemsUnderThisItem is so that a selected annotation's
    // grips can still be used even if the grips are behind another annotation
    const itemsUnderThisItem = selectedItems.filter((annotation) => annotation.index < item.index);

    item.on('click', (event: paper.MouseEvent) => {
      const { ctrlKey } = getModifierKeys(event.event);

      if (item.annotationIsEditableByCurrentUser) {
        // Stop propagation to prevent this event from being fired on the layer,
        //   which will deselect all selected items
        event.stopPropagation();

        if (event.event.detail > 1) {
          // Double click
          if (!item.locked) eventHandlers.doubleClick(item);
        } else if (!item.beingDragged) {
          if (!item.locked) eventHandlers.singleClick(item, ctrlKey);
          annotationRotation = item.rotation;
        }
      }
    });

    item.on('mousedown', (event: paper.MouseEvent) => {
      item.beingDragged = null;
      if (item.reduxSelected) {
        item.gripBeingDragged = getGripUnderPoint(event.point, item);
        annotationRotation = item.rotation;
      }

      for (const annotation of itemsUnderThisItem) {
        annotation.beingDragged = false;
        annotation.gripBeingDragged = getGripUnderPoint(event.point, annotation);
      }
    });

    item.on('mouseup', (event: paper.MouseEvent) => {
      setCursor('auto');

      const itemsBeingResized = [item].concat(itemsUnderThisItem).filter((paperItem) => paperItem.gripBeingDragged);
      if (itemsBeingResized.length === 1) {
        eventHandlers.dragEnd(itemsBeingResized[0]);
      } else if (itemsBeingResized.length > 1) {
        throw new Error('Multiple items marked as having a grip being dragged.  This should never happen');
      } else if (currentlyDragging) {
        eventHandlers.mouseUp(item, getModifierKeys(event.event).ctrlKey);
      }

      item.gripBeingDragged = null;
      currentlyDragging = false;
    });

    item.on('mousemove', (event: paper.MouseEvent) => {
      if (dialogIsOpen) {
        return;
      }

      if (itemsUnderThisItem.length === 0 && item.annotationIsEditableByCurrentUser) {
        onHoverItem(item, getGripUnderPoint(event.point, item), event);

        return;
      }

      for (const itemUnderThisItem of itemsUnderThisItem) {
        const gripUnderItem = getGripUnderPoint(event.point, itemUnderThisItem);

        if (gripUnderItem && itemUnderThisItem.annotationIsEditableByCurrentUser) {
          const simulatedEvent = { ...event, point: event.point, delta: event.delta, target: itemUnderThisItem };

          onHoverItem(itemUnderThisItem, gripUnderItem, simulatedEvent);
        }
      }
    });

    item.on('mouseenter', () => {
      if (dialogIsOpen) {
        return;
      }

      if (!item.reduxSelected && !item.locked && item.annotationIsEditableByCurrentUser) {
        item.selectedColor = paper.project.activeLayer.selectedColor;
        item.selected = true;
      }
    });

    item.on('mouseleave', (event: paper.MouseEvent) => {
      if (dialogIsOpen) {
        return;
      }

      if (item.annotationIsEditableByCurrentUser) {
        onLeaveItem(item, event.event);
      }
    });

    item.on('mousedrag', (event: paper.MouseEvent) => {
      currentlyDragging = true;

      event.stopPropagation();

      if (event.delta.x !== 0 || event.delta.y !== 0) {
        item.beingDragged = item;

        const hiddenGripItem = itemsUnderThisItem.find((itemUnder) => itemUnder.gripBeingDragged);

        let gripBeingDragged;

        if (item.gripBeingDragged && item.reduxSelected) {
          ({ gripBeingDragged } = item);
        } else if (hiddenGripItem) {
          ({ gripBeingDragged } = hiddenGripItem);
        } else if (item.annotationIsEditableByCurrentUser) {
          onDragItem(event, item);
          eventHandlers.dragItem(item);
        }

        if (item.translationArea) {
          renderTranslationAreaBounds(item, pageImageInfo);
        }

        if (gripBeingDragged != null) {
          for (const selectedItem of selectedItems) {
            onDragItemCorner(selectedItem, gripBeingDragged, {
              ...event,
              point: event.point,
              delta: event.delta,
              target: selectedItem,
            });

            if (selectedItem.className !== 'PointText') {
              onDragItemCenter(selectedItem, gripBeingDragged, {
                ...event,
                point: event.point,
                delta: event.delta,
                target: selectedItem,
              });
            }

            eventHandlers.dragItem(selectedItem);
          }
        }

        // This fixes the flipping bug if the user drags the item to quickly
        if (item.scaling.x < 0 || item.scaling.y < 0) {
          item.scaling = {
            x: item.scaling.x * -1,
            y: item.scaling.y * -1,
          };
        }
      }
    });
  }
}

/* eslint no-else-return: 0 */
function getCursor(grip: GripName | undefined, itemRotation: number, allowCenterCursor: boolean) {
  let angle;
  if (itemRotation > 0) {
    angle = -180 - (180 - itemRotation);
  } else {
    angle = itemRotation;
  }
  const rotation = -angle;
  // Any rotation over 45 degrees will re-assign the labels of the corners, therefore,
  // they will appear tilted only between the values of 22.5 and 45
  const isTilted = rotation % 45 >= 22.5;
  const forward = rotation % 90 >= 45;

  if (grip === 'topRight' || grip === 'bottomLeft') {
    if ((!isTilted && !forward) || (isTilted && forward)) {
      return 'nesw-resize';
    } else if (isTilted && !forward) {
      return 'ns-resize';
    } else {
      return 'ew-resize';
    }
  } else if (grip === 'bottomRight' || grip === 'topLeft') {
    if ((!isTilted && !forward) || (isTilted && forward)) {
      return 'nwse-resize';
    } else if (isTilted && !forward) {
      return 'ew-resize';
    } else {
      return 'ns-resize';
    }
  }

  if (allowCenterCursor) {
    if (grip === 'topCenter' || grip === 'bottomCenter') {
      if ((!isTilted && !forward) || (isTilted && forward)) {
        return 'ns-resize';
      } else if (isTilted && !forward) {
        return 'nwse-resize';
      } else {
        return 'nesw-resize';
      }
    } else if (grip === 'rightCenter' || grip === 'leftCenter') {
      if ((!isTilted && !forward) || (isTilted && forward)) {
        return 'ew-resize';
      } else if (isTilted && !forward) {
        return 'nesw-resize';
      } else {
        return 'nwse-resize';
      }
    }
  }

  return 'auto';
}

function onHoverItem(item: RenderedItem, grip: GripName | null | undefined, event: PaperMouseEvent) {
  if (item.category !== 'seal' && item.annotationIsEditableByCurrentUser && item.reduxSelected) {
    if (grip) {
      setCursor(getCursor(grip, item.rotation, item.className !== 'PointText'));
    } else if (item.className === 'PointText') {
      setCursor('text');
    } else {
      setCursor('auto');
    }
  } else if (event.event.buttons === 0 || document.body.style.cursor !== 'move') {
    setCursor('auto');
  }
}

function onLeaveItem(item: RenderedItem, event: MouseEvent) {
  if (event.buttons === 0) {
    setCursor('auto');
  }

  if (!item.reduxSelected) {
    item.selected = false;
  }
}

interface PaperMouseEvent extends Omit<paper.MouseEvent, 'preventDefault' | 'stopPropagation' | 'stop' | 'toString'> {}

interface PaperMouseEventWithTarget extends PaperMouseEvent {
  readonly target: RenderedItem;
}

function onDragItemCorner(item: RenderedItem, corner: GripName, event: PaperMouseEventWithTarget) {
  if (item.category === 'seal') {
    return;
  }

  // We receive delta in terms of the numbers of pixels the mouse moved on the x and y axis.
  const { x, y } = event.delta;

  // Translating this into a resize delta requires knowing which corner the drag event started on.
  let delta = 0;
  switch (corner) {
    case 'topRight':
      delta = x - y;
      break;

    case 'bottomRight':
      delta = x + y;
      break;

    case 'topLeft':
      delta = -x - y;
      break;

    case 'bottomLeft':
      delta = -x + y;
      break;

    // for when it's not the corner, but the center
    default:
      return;
  }

  // paper.js scales using percentages of the item's total size
  const scalePercentage = 1 + delta / (item.bounds.width + item.bounds.height);

  scaleItem(item, scalePercentage, corner);
}

function onDragItemCenter(item: RenderedItem, center: GripName, event: PaperMouseEventWithTarget) {
  if (item.category === 'seal' || isCorner(center)) {
    return;
  }

  // Paths are a special case: they have no scaling property and are resized differently
  if (item instanceof paper.Path) {
    onDragPathGrip(item, center, event);
    return;
  }

  const directionModifier = (isVertical(center) ? event.delta.x > 0 : event.delta.y > 0) ? 1 : -1;

  const delta = Math.hypot(event.delta.x, event.delta.y);
  const length = isVertical(center) ? item.bounds.width : item.bounds.height;

  // paper.js scales using percentages of the item's total size
  const scalingPercentage = (delta / length) * directionModifier;

  stretchItem(item, scalingPercentage, center);
}

function getAngle() {
  if (annotationRotation > 0) {
    return 360 - annotationRotation;
  } else {
    return -annotationRotation;
  }
}

function onDragPathGrip(item: paper.Path, gripName: SideGripName, event: PaperMouseEventWithTarget) {
  const oldGripPosition = event.point.subtract(event.delta);
  const newGripPosition = event.point;

  const draggedSegment = minBy(item.segments, (segment) => segment.point.getDistance(oldGripPosition))!;
  const otherSegment = item.segments.find((segment) => segment !== draggedSegment)!;

  let newEndpoint;
  if ((item as unknown as RenderedItem).restrictOrthogonal || event.event.altKey) {
    newEndpoint = projectOntoLine(otherSegment.point, draggedSegment.point, newGripPosition);
  } else {
    newEndpoint = draggedSegment.point.add(event.delta);
  }

  draggedSegment.point = newEndpoint;
}

function isItemRotated() {
  const angle = getAngle();

  return (angle >= 45 && angle < 135) || (angle >= 225 && angle < 315);
}

function scaleTextItem(item: paper.PointText, scalePercentage: number) {
  // TODO: this should be based on corner name instead of just arbitrarily growing
  item.fontSize = Math.max((item.fontSize as number) * scalePercentage, MINIMUM_FONT_SIZE);
  item.leading = Math.max(item.leading * scalePercentage, MINIMUM_FONT_SIZE);
}

function scalePathItem(item: paper.Path, scalePercentage: number) {
  if (item.segments.length !== 2) {
    throw new Error('Complex paths are currently not supported');
  }

  const pointA = Point2D.fromObject(item.segments[0].point);
  const pointB = Point2D.fromObject(item.segments[1].point);

  const pathLength = pointA.distanceTo(pointB);

  let totalGrowth = pathLength * Math.abs(1 - scalePercentage);
  if (scalePercentage < 1) {
    totalGrowth = -totalGrowth;
  }

  const newPointA = Line2D.fromPoints(pointA, pointB).getPointAlongLine(pathLength + totalGrowth / 2);
  const newPointB = Line2D.fromPoints(pointB, pointA).getPointAlongLine(pathLength + totalGrowth / 2);

  item.segments[0].point = toPaperPoint(newPointA);
  item.segments[1].point = toPaperPoint(newPointB);
}

function scaleGenericItem(item: RenderedItem, scalePercentage: number, cornerName: CornerGripName | null) {
  const { width, height } = item.bounds;
  const smallestDimensionAfterScale = Math.min(width * scalePercentage, height * scalePercentage);
  if (smallestDimensionAfterScale < MINIMUM_ITEM_SIZE) {
    return;
  }

  if (cornerName == null) {
    item.scale(scalePercentage);

    return;
  }

  const fixedCornerName = getOppositeCorner(cornerName);
  const fixedCornerPosition = getAnnotationGrips(item)[fixedCornerName];

  item.scale(scalePercentage, fixedCornerPosition);
}

export function scaleItem(item: RenderedItem, scalePercentage: number, cornerName: CornerGripName | null = null) {
  if (item.className === 'PointText') {
    scaleTextItem(item as unknown as paper.PointText, scalePercentage);
  } else if (item instanceof paper.Path) {
    scalePathItem(item, scalePercentage);
  } else {
    scaleGenericItem(item, scalePercentage, cornerName);
  }
}

export function stretchItem(item: RenderedItem, scalingPercentage: number, centerName: SideGripName) {
  const fixedSideName = getOppositeSide(centerName);
  const fixedSidePosition = getAnnotationGrips(item)[fixedSideName];

  let widthScale = 1;
  let heightScale = 1;

  switch (centerName) {
    case 'topCenter':
      heightScale -= scalingPercentage;
      break;

    case 'bottomCenter':
      heightScale += scalingPercentage;
      break;

    case 'leftCenter':
      widthScale -= scalingPercentage;
      break;

    case 'rightCenter':
      widthScale += scalingPercentage;
      break;

    default:
      throw new Error(`Invalid center name: ${centerName}`);
  }

  if (isItemRotated()) {
    [widthScale, heightScale] = [heightScale, widthScale];
  }

  item.rotate(-annotationRotation, fixedSidePosition);
  item.scale(widthScale, heightScale, fixedSidePosition);
  item.rotate(annotationRotation, fixedSidePosition);
}

function onDragItem(event: paper.MouseEvent, item: RenderedItem) {
  const selectedItems = getReduxSelectedItems();

  // Handles case where item was hovered (in which case it was not redux selected yet)
  if (!selectedItems.includes(item)) {
    selectedItems.push(item);
  }

  setCursor('move');

  const delta = new paper.Point(event.delta.x, event.delta.y);
  for (const selectedItem of selectedItems) {
    selectedItem.translate(delta);
  }
}

function getReduxSelectedItems() {
  return paper.project.activeLayer.children.filter((child) => (child as RenderedItem).reduxSelected);
}

function setCursor(cursor: string) {
  if (document.body.style.cursor !== cursor) {
    document.body.style.cursor = cursor;
  }
}
